import { useState } from "react";
import { ShieldCheck, Clock, Home, Package, Download, CreditCard, MessageSquareQuote } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useProducts } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";

const productIcons = [ShieldCheck, Clock, Home];

const productFeatures: Record<string, string[]> = {
  "Family Emergency Psychological Survival Guide": [
  "How to explain war to a child (by age group)",
  "10 Immediate calming techniques",
  "Nighttime anxiety protocol",
  "Printable reassurance scripts",
  "Family stability routine template"],

  "Conflict Preparedness Starter Kit – First 72 Hours": [
  "72-hour emergency checklist",
  "Go-bag template",
  "Water & food calculation table",
  "Document protection checklist",
  "Family regroup protocol"],

  "Stay Safe Without Leaving: Home Safety Protocol": [
  "Indoor safety procedures",
  "Blackout preparation guide",
  "Communication redundancy plan",
  "Panic management techniques",
  "Cyber attack awareness"]

};

const stripeNameMap: Record<string, string> = {
  "Family Emergency Psychological Survival Guide": "Psychological Survival Guide",
  "Conflict Preparedness Starter Kit – First 72 Hours": "72-Hour Kit Checklist",
  "Stay Safe Without Leaving: Home Safety Protocol": "Home Safety Protocol"
};

const EmergencyKits = () => {
  const { data: products } = useProducts();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { formatUsdPriceWithUsd } = useLocalCurrency();

  const displayProducts = (products || []).filter(p => p.name !== "Family Conflict Survival Bundle");

  const handleBuy = async (productName: string, id: string) => {
    if (!user) {navigate("/auth");return;}
    setLoadingId(id);
    try {
      const stripeName = stripeNameMap[productName] || productName;
      const { data, error } = await supabase.functions.invoke("create-payment", {
        body: { productName: stripeName }
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoadingId(null);
    }
  };

  const handleBuyBundle = async () => {
    if (!user) {navigate("/auth");return;}
    setLoadingId("bundle");
    try {
      const { data, error } = await supabase.functions.invoke("create-payment", {
        body: { productName: "Family Conflict Survival Bundle" }
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <section id="kits" className="border-b border-border">
      <div className="container mx-auto px-4 py-8 sm:py-12">
        {/* Contextual hook */}
        <p className="mb-1 text-center text-sm font-semibold text-warning">
          If you have family in risk areas, start here
        </p>
        <p className="mb-4 text-center text-xs text-muted-foreground">
          Use the live map to track threats. Use these guides to prepare your family calmly and practically.
        </p>

        <div className="mb-6 sm:mb-8 text-center">
          <Badge variant="outline" className="mb-2 sm:mb-3 border-warning/30 text-warning">
            <Package className="mr-1 h-3 w-3" />
            Digital Survival Guides
          </Badge>
          <h2 className="mb-2 text-xl sm:text-2xl font-bold text-foreground md:text-3xl">
            Emergency Preparedness Kits
          </h2>
          <p className="mx-auto max-w-lg text-xs sm:text-sm text-muted-foreground px-2">
            Practical, calm step-by-step plans to protect your family mentally and physically during conflicts. Instant download after purchase.
          </p>
        </div>

        {/* Bundle CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-6 text-center">

          <div className="mb-1 font-mono text-xs font-medium uppercase tracking-wider text-primary">Best Value</div>
          <Badge variant="outline" className="mb-2 border-primary/30 text-primary">⭐ Recommended</Badge>
          <h3 className="mb-1 text-xl font-bold text-foreground">Family Conflict Survival Bundle</h3>
          <p className="mb-3 text-sm text-muted-foreground">All 3 guides — one price</p>

          {/* Bundle-specific bullets */}
          <ul className="mx-auto mb-4 max-w-md space-y-1.5 text-left text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              Full family plan: emotional, practical, and home safety in one pack
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              Print-ready checklists and scripts
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              Designed for non-experts; easy to apply under stress
            </li>
          </ul>

          <div className="mb-4 flex items-center justify-center gap-3">
            <span className="text-sm text-muted-foreground line-through">$40+</span>
            <span className="text-3xl font-black text-primary">{formatUsdPriceWithUsd(16.90)}</span>
          </div>
          <div className="flex justify-center">
            <Button size="lg" className="font-semibold" onClick={handleBuyBundle} disabled={loadingId === "bundle"}>
              {loadingId === "bundle" ? "Loading..." : `Secure my family plan — ${formatUsdPriceWithUsd(16.90)}`}
            </Button>
          </div>

          {/* Micro-badges */}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Download className="h-3 w-3" />
              Instant PDF download
            </span>
            <span className="flex items-center gap-1">
              <CreditCard className="h-3 w-3" />
              One-time purchase, no subscription
            </span>
          </div>
        </motion.div>

        {/* Example scenario testimonial */}
        <div className="mb-8 mx-auto max-w-lg rounded-lg border border-border bg-card/50 p-4">
          <div className="flex items-start gap-3">
            <MessageSquareQuote className="mt-0.5 h-5 w-5 shrink-0 text-primary/60" />
            <div>
              <p className="text-sm italic text-muted-foreground">
                "I used these scripts to explain missile sirens to my 7-year-old in a calm way. The go-bag checklist gave us structure when everything felt chaotic."
              </p>
              <p className="mt-2 text-xs font-medium text-muted-foreground/70">— Aviv A.</p>
            </div>
          </div>
        </div>

        {/* Individual cards */}
        <div className="grid gap-4 md:grid-cols-3">
          {displayProducts.map((product, i) => {
            const Icon = productIcons[i] || ShieldCheck;
            const features = productFeatures[product.name] || [];
            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}>

                <Card className="flex h-full flex-col border-border bg-card">
                  <CardHeader className="pb-3">
                    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-base leading-tight">{product.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{product.description}</p>
                  </CardHeader>
                  <CardContent className="flex-1 pb-3">
                    <ul className="space-y-1.5">
                      {features.map((f) =>
                      <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-primary" />
                          {f}
                        </li>
                      )}
                    </ul>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <div className="flex w-full items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-lg font-bold text-foreground">{formatUsdPriceWithUsd(product.price_usd)}</span>
                        <span className="text-[10px] text-muted-foreground">individual value</span>
                      </div>
                      <Badge variant="secondary" className="pointer-events-none text-xs">
                        Included in bundle
                      </Badge>
                    </div>
                  </CardFooter>
                </Card>
              </motion.div>);

          })}
        </div>
      </div>
    </section>);

};

export default EmergencyKits;